import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

import { CommunicationType } from "@/enums/communicationLogs";
import { ShortcutAction } from "@/enums/keyboardShortcuts";
import {
  INBOX_SHORTCUT_NEXT_RECORD,
  INBOX_SHORTCUT_PREVIOUS_RECORD,
  INBOX_SHORTCUT_REPLY,
} from "@/helpers/constants/events";
import { useEmitter } from "@/hooks/common";
import { useKeyboardShortcuts } from "@/hooks/keyboardShortcuts";
import { ROUTE_INBOX } from "@/router/routes";

import type { CommunicationLogBase } from "@/models/communicationLogs";
import type { Ref, ShallowRef } from "vue";

interface UseInboxKeyboardShortcutsOptions {
  allDataPages: Ref<CommunicationLogBase[] | undefined>;
  activeThread: Ref<CommunicationLogBase | undefined>;
  isOpen: Ref<boolean>;
  infiniteScrollWrapper: Readonly<ShallowRef<HTMLDivElement | null>>;
  toggleThread: (id: number, target?: "data") => void;
  closeThread: () => void;
  incrementThread: (step: number) => void;
}

export const useInboxKeyboardShortcuts = ({
  allDataPages,
  activeThread,
  isOpen,
  infiniteScrollWrapper,
  toggleThread,
  closeThread,
  incrementThread,
}: UseInboxKeyboardShortcutsOptions) => {
  const route = useRoute();
  const router = useRouter();
  const emitter = useEmitter();
  const { assignShortcutToAction } = useKeyboardShortcuts();

  const isInboxRoute = computed(() => route.name === ROUTE_INBOX);

  const canPerformInboxAction = () => {
    if (!isInboxRoute.value) {
      return false;
    }

    const activeElement = document.activeElement;
    if (activeElement) {
      const tagName = activeElement.tagName.toLowerCase();
      if (
        tagName === "input" ||
        tagName === "textarea" ||
        activeElement.getAttribute("contenteditable") === "true"
      ) {
        return false;
      }
    }

    const dialog = document.querySelector('[role="dialog"]');
    if (dialog) {
      return false;
    }

    return true;
  };

  const navigateToTab = (type?: CommunicationType) => {
    if (type === CommunicationType.note) {
      const query = { type };
      router.push({ name: ROUTE_INBOX, query });
      return;
    }
    const query = type ? { type, view: "inbox" } : { view: "inbox" };
    router.push({ name: ROUTE_INBOX, query });
  };

  const jumpToThread = (index: number) => {
    if (!allDataPages.value?.length) {
      return;
    }

    const thread = allDataPages.value[index];
    if (thread) {
      toggleThread(thread.id);
      const row = document.getElementById(`row-${index}`);
      if (row && infiniteScrollWrapper.value) {
        infiniteScrollWrapper.value.scrollTo({
          top: row.offsetTop,
          behavior: "smooth",
        });
      }
    }
  };

  const jumpToTop = () => {
    jumpToThread(0);
  };

  const jumpToBottom = () => {
    if (!allDataPages.value?.length) {
      return;
    }
    jumpToThread(allDataPages.value.length - 1);
  };

  const handleNavigateUp = () => {
    if (isOpen.value) {
      incrementThread(-1);
    } else if (allDataPages.value?.length) {
      const currentIndex = activeThread.value
        ? allDataPages.value.findIndex(
            (log) => log.id === activeThread.value?.id
          )
        : 0;
      const newIndex = Math.max(0, currentIndex - 1);
      const thread = allDataPages.value[newIndex];
      if (thread) {
        toggleThread(thread.id);
      }
    }
  };

  const handleNavigateDown = () => {
    if (isOpen.value) {
      incrementThread(1);
    } else if (allDataPages.value?.length) {
      const currentIndex = activeThread.value
        ? allDataPages.value.findIndex(
            (log) => log.id === activeThread.value?.id
          )
        : -1;
      const newIndex = Math.min(
        allDataPages.value.length - 1,
        currentIndex + 1
      );
      const thread = allDataPages.value[newIndex];
      if (thread) {
        toggleThread(thread.id);
      }
    }
  };

  const handleOpenThread = () => {
    if (!isOpen.value && allDataPages.value?.length) {
      const thread = activeThread.value || allDataPages.value[0];
      if (thread) {
        toggleThread(thread.id);
      }
    }
  };

  assignShortcutToAction(
    ShortcutAction.InboxNextThread,
    () => incrementThread(1),
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxPreviousThread,
    () => incrementThread(-1),
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxCloseThread,
    closeThread,
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxNavigateUp,
    handleNavigateUp,
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxNavigateDown,
    handleNavigateDown,
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxOpenThread,
    handleOpenThread,
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxJumpToTop,
    jumpToTop,
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxJumpToBottom,
    jumpToBottom,
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxGoToAllActivities,
    () => navigateToTab(),
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxGoToEmails,
    () => navigateToTab(CommunicationType.email),
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxGoToSms,
    () => navigateToTab(CommunicationType.sms),
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxGoToCalls,
    () => navigateToTab(CommunicationType.call),
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxGoToTasks,
    () => navigateToTab(CommunicationType.task),
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxGoToNotes,
    () => navigateToTab(CommunicationType.note),
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxReply,
    () => {
      if (isOpen.value) {
        emitter.emit(INBOX_SHORTCUT_REPLY);
      }
    },
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxNextRecord,
    () => {
      if (isOpen.value) {
        emitter.emit(INBOX_SHORTCUT_NEXT_RECORD);
      }
    },
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxPreviousRecord,
    () => {
      if (isOpen.value) {
        emitter.emit(INBOX_SHORTCUT_PREVIOUS_RECORD);
      }
    },
    canPerformInboxAction
  );
};
